import { c as _c } from "react/compiler-runtime";
import { CheckCircleIcon, CloseCircleIcon, InfoIcon, XCircleIcon } from '@systemeio/ui/icons';
import { ActionMenu, ActionMenuItem, ActionMenuItemProps } from '@systemeio/ui';
import ItemCount from 'shared/components/item-count';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { twMerge } from 'tailwind-merge';
import { getNotificationIcon, ruleIconSizeClassName } from '../constants/notification-icons';
import { useNotificationsActions } from '../hooks/use-notification-actions';
import { NotificationBaseItemInterface, NotificationItemInterface, NotificationSharingItemInterface } from '../types/notification-types';
import { NotificationTypeEnum } from 'modules/profile/notification-settings/enums/notification-settings-enum';
import { SharingStatusEnum } from 'modules/share/enum/sharing-status-enum';
import { ReactNode } from 'react';
import { dateTimeFormat } from 'shared/utils/date-time-format';
import { SharingFailReasonEnum } from 'modules/share/enum/sharing-fail-reason-enum';
import { useSharingStatusPolling } from 'modules/share/hooks/use-sharing-status-polling';
import { useCancelSharing } from 'modules/share/hooks/use-cancel-sharing';
import { getWorkflowEditorUrl } from 'shared/utils/get-workflow-editor-url';
export interface NotificationItemViewProps {
  isRead?: boolean;
  icon?: ReactNode;
  message: string;
  description?: ReactNode;
  showCancelButton?: boolean;
  isCancelling?: boolean;
  actions?: ActionMenuItemProps[];
  onClick?: () => void;
  onCancel?: () => void;
}
export const NotificationItemView = t0 => {
  const $ = _c(26);
  const {
    isRead,
    icon,
    message,
    description,
    showCancelButton,
    isCancelling,
    actions,
    onClick,
    onCancel
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const isActions = actions && actions.length > 0;
  const t1 = isRead && "text-text-secondary group-hover:text-text-primary";
  const t2 = onClick && "cursor-pointer";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = twMerge("bg-background-secondary border-text-secondary text-text-primary hover:bg-brand-primary-subtle group flex flex-row justify-between border-b px-6 py-4 transition last:border-b-0", t1, t2);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== message) {
    t4 = <div><span className="group-hover:text-text-primary transition" dangerouslySetInnerHTML={{
        __html: message
      }} /></div>;
    $[3] = message;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== description || $[6] !== t4) {
    t5 = <div className="text-start mr-auto w-full">{t4}{description}</div>;
    $[5] = description;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== isCancelling || $[9] !== onCancel || $[10] !== showCancelButton) {
    t6 = showCancelButton && <button className={twMerge("group/cancel", isCancelling && "animate-pulse cursor-progress")} disabled={isCancelling} onClick={e => {
      e.stopPropagation();
      onCancel?.();
    }}><XCircleIcon className="group-hover/cancel:[&>path]:fill-brand-primary [&>path]:main-transition-colors h-5 w-5" /></button>;
    $[8] = isCancelling;
    $[9] = onCancel;
    $[10] = showCancelButton;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== icon || $[13] !== t5 || $[14] !== t6) {
    t7 = <div className="inline-flex w-full flex-row items-center gap-4">{icon}{t5}{t6}</div>;
    $[12] = icon;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== actions || $[17] !== isActions || $[18] !== isRead || $[19] !== t) {
    t8 = (!isRead || isActions) && <div className="inline-flex flex-row items-center gap-4 pl-3">{!isRead && <ItemCount>{t("dashboard.notifications.new")}</ItemCount>}{isActions && <ActionMenu menuItems={actions.map(_temp)} />}</div>;
    $[16] = actions;
    $[17] = isActions;
    $[18] = isRead;
    $[19] = t;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== onClick || $[22] !== t3 || $[23] !== t7 || $[24] !== t8) {
    t9 = <div className={t3} onClick={onClick}>{t7}{t8}</div>;
    $[21] = onClick;
    $[22] = t3;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
};
export interface SharingNotificationProps {
  notification: NotificationSharingItemInterface;
}
const SharingNotificationItem = t0 => {
  const $ = _c(31);
  const {
    notification: notificationItem
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  const {
    cancelSharing,
    isCancelling
  } = useCancelSharing();
  const t1 = notificationItem.data.status === SharingStatusEnum.Running;
  let t2;
  if ($[0] !== notificationItem.data.sharingId || $[1] !== t1) {
    t2 = {
      sharingId: notificationItem.data.sharingId,
      allowFetch: t1
    };
    $[0] = notificationItem.data.sharingId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    sharingStatus,
    mutate
  } = useSharingStatusPolling(t2);
  let T0;
  let handleCancel;
  let handleNotificationClick;
  let notification;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[3] !== cancelSharing || $[4] !== mutate || $[5] !== notificationItem || $[6] !== sharingStatus || $[7] !== t || $[8] !== user) {
    notification = {
      ...notificationItem,
      data: {
        ...notificationItem.data,
        ...(sharingStatus ?? {})
      }
    };
    const sharedResourceId = notification.data.sharedResourceId;
    handleNotificationClick = notification.data.status === SharingStatusEnum.Success && sharedResourceId ? async () => {
      window.open(getWorkflowEditorUrl(sharedResourceId), "_blank");
    } : undefined;
    handleCancel = async () => {
      await cancelSharing(notification.data.sharingId);
      await mutate(_temp2, false);
    };
    const getIcon = () => {
      switch (notification.data.status) {
        case SharingStatusEnum.Running:
          {
            return <InfoIcon className={twMerge(ruleIconSizeClassName, "fill-yellow")} />;
          }
        case SharingStatusEnum.Failed:
          {
            if (notification.data.failReason === SharingFailReasonEnum.CancelledByUser) {
              return <CloseCircleIcon className={ruleIconSizeClassName} />;
            } else {
              return <CloseCircleIcon className={twMerge(ruleIconSizeClassName, "fill-red")} />;
            }
          }
        case SharingStatusEnum.Success:
          {
            return <CheckCircleIcon className={twMerge(ruleIconSizeClassName, "fill-green")} />;
          }
        default:
          {
            return <InfoIcon className={twMerge(ruleIconSizeClassName, "fill-yellow")} />;
          }
      }
    };
    const getMessage = () => {
      switch (notification.data.status) {
        case SharingStatusEnum.Running:
          {
            return t("dashboard.notifications.workflow_sharing_in_progress");
          }
        case SharingStatusEnum.Failed:
          {
            switch (notification.data.failReason) {
              case SharingFailReasonEnum.CancelledByUser:
                {
                  return t("dashboard.notifications.workflow_sharing_cancelled");
                }
              case SharingFailReasonEnum.FailedToShare:
                {
                  return t("dashboard.notifications.workflow_sharing_failed_to_share");
                }
              case SharingFailReasonEnum.ProcessingTimeExceeded:
                {
                  return t("dashboard.notifications.workflow_sharing_processing_time_exceeded");
                }
              case SharingFailReasonEnum.ResourceRemoved:
                {
                  return t("dashboard.notifications.workflow_sharing_resource_removed");
                }
              default:
                {
                  return t("dashboard.notifications.workflow_sharing_failed");
                }
            }
          }
        case SharingStatusEnum.Success:
          {
            return t("dashboard.notifications.workflow_sharing_completed");
          }
      }
    };
    const getDescription = () => {
      switch (notification.data.status) {
        case SharingStatusEnum.Running:
          {
            return notification.data?.progressPercent !== undefined && <div className="flex items-center gap-3"><div className="bg-background-tertiary relative h-1 w-full max-w-[342px] rounded-full"><div className="bg-brand-primary absolute left-0 top-0 h-full rounded-full transition-[width] duration-300 ease-in-out" style={{
                  width: `${notification.data.progressPercent}%`
                }} /></div><div className="text-text-primary text-xs font-medium">{notification.data.progressPercent}%</div></div>;
          }
        case SharingStatusEnum.Failed:
        case SharingStatusEnum.Success:
          {
            return user && <div className="group-hover:text-text-primary transition">{dateTimeFormat(notification.data.updatedAt, {
                timeFormat: user.timeFormat,
                dateFormat: user.dateFormat,
                timeZone: user.timezone
              })}</div>;
          }
      }
    };
    T0 = NotificationItemView;
    t3 = notification.read;
    if ($[17] !== getIcon) {
      t4 = getIcon();
      $[17] = getIcon;
      $[18] = t4;
    } else {
      t4 = $[18];
    }
    if ($[19] !== getMessage) {
      t5 = getMessage();
      $[19] = getMessage;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    t6 = getDescription();
    $[3] = cancelSharing;
    $[4] = mutate;
    $[5] = notificationItem;
    $[6] = sharingStatus;
    $[7] = t;
    $[8] = user;
    $[9] = T0;
    $[10] = handleCancel;
    $[11] = handleNotificationClick;
    $[12] = notification;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    T0 = $[9];
    handleCancel = $[10];
    handleNotificationClick = $[11];
    notification = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
  }
  const t7 = notification.data.status === SharingStatusEnum.Running;
  let t8;
  if ($[21] !== T0 || $[22] !== handleCancel || $[23] !== handleNotificationClick || $[24] !== isCancelling || $[25] !== t3 || $[26] !== t4 || $[27] !== t5 || $[28] !== t6 || $[29] !== t7) {
    t8 = <T0 isRead={t3} icon={t4} message={t5} description={t6} showCancelButton={t7} isCancelling={isCancelling} onClick={handleNotificationClick} onCancel={handleCancel} />;
    $[21] = T0;
    $[22] = handleCancel;
    $[23] = handleNotificationClick;
    $[24] = isCancelling;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  return t8;
};
export interface BaseNotificationProps {
  notification: NotificationBaseItemInterface;
  actions?: ActionMenuItemProps[];
}
const BaseNotificationItem = t0 => {
  const $ = _c(14);
  const {
    notification
  } = t0;
  const {
    user
  } = useUser();
  const {
    getNotificationLink
  } = useNotificationsActions();
  let t1;
  if ($[0] !== getNotificationLink || $[1] !== notification.id) {
    t1 = async () => {
      const {
        link
      } = await getNotificationLink(notification.id);
      window.open(link, "_blank");
    };
    $[0] = getNotificationLink;
    $[1] = notification.id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleNotificationClick = t1;
  const t2 = notification.read;
  let t3;
  if ($[3] !== notification) {
    t3 = getNotificationIcon(notification);
    $[3] = notification;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== notification.createdAt || $[6] !== user) {
    t4 = user && <div className="group-hover:text-text-primary transition">{dateTimeFormat(notification.createdAt, {
        timeFormat: user.timeFormat,
        dateFormat: user.dateFormat,
        timeZone: user.timezone
      })}</div>;
    $[5] = notification.createdAt;
    $[6] = user;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== handleNotificationClick || $[9] !== notification.message || $[10] !== notification.read || $[11] !== t3 || $[12] !== t4) {
    t5 = <NotificationItemView isRead={t2} icon={t3} message={notification.message} description={t4} onClick={handleNotificationClick} />;
    $[8] = handleNotificationClick;
    $[9] = notification.message;
    $[10] = notification.read;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};
export interface NotificationItemProps {
  notification: NotificationItemInterface;
  actions?: ActionMenuItemProps[];
}
export const NotificationItem = t0 => {
  const $ = _c(5);
  const {
    notification,
    actions
  } = t0;
  switch (notification.eventType) {
    case NotificationTypeEnum.SharingStarted:
      {
        let t1;
        if ($[0] !== notification) {
          t1 = <SharingNotificationItem notification={notification} />;
          $[0] = notification;
          $[1] = t1;
        } else {
          t1 = $[1];
        }
        return t1;
      }
    default:
      {
        let t1;
        if ($[2] !== actions || $[3] !== notification) {
          t1 = <BaseNotificationItem notification={notification} actions={actions} />;
          $[2] = actions;
          $[3] = notification;
          $[4] = t1;
        } else {
          t1 = $[4];
        }
        return t1;
      }
  }
};
function _temp(action) {
  return <ActionMenuItem key={`${action.label}-na-action`} label={action.label} onClick={action.onClick} />;
}
function _temp2(data) {
  if (!data) {
    return;
  }
  return {
    ...data,
    status: SharingStatusEnum.Failed,
    failReason: SharingFailReasonEnum.CancelledByUser
  };
}